import {
  getClientFeatureFlagEnabled,
  getClientFeatureFlagDisplayValue,
  mergeClientFeatureFlag,
} from './ClientFeatureFlagUtils'
import constants from '../constants/constant'

export const CLIENT_DASHBOARD_V3_FLAG = 'client-dashboard-v3'
export const WEEKLY_PLAN_UI_FLAG = 'weekly-plan-ui'

function encodePathSegment(value) {
  return encodeURIComponent(String(value ?? ''))
}

export function getUserDashboardV3Preference(user) {
  // SPS-5593 hotfix 2026-09-24 — removal in SPS-<follow-up>.
  //
  // Prod served the v2 dashboard to users who should have been on v3. Everything
  // below still works; the problem is what it decides.
  //
  // The incident itself was mitigated in data on 2026-09-24 — 131 prod users at
  // v3_user = 0 were updated, none remain. This stays because that fix is a
  // point-in-time sweep of a column that defaults to 0: the last one ran around
  // 2026-07-29 and 131 users accumulated behind it. Until the gate is removed
  // outright, THIS is what stops the next new user landing on v2. Both gates in this file
  // derive from this function — getUserDashboardV3Enabled is
  // `preference === true`, and getClientDashboardV3Enabled returns early on a
  // true preference before it ever reads the client flag — so one early return
  // here is the whole switch, and there is no second place to forget.
  //
  // Two paths produced v2, and this covers both:
  //   - auth_users.v3_user = 0 / absent, which is the column default, and
  //   - `if (userPreference === false) return false` below, which short-circuits
  //     BEFORE the client flag, so a client with client-dashboard-v3 = 1 still
  //     served v2 to any user whose column said 0.
  // V3SiteLayout then actively redirects such a user off /v3/... to the legacy
  // dashboard, which is what people saw.
  //
  // The flag reader below is deliberately left intact and the v2 pages stay
  // routable (preferredMode: 'legacy' still resolves to them). Deleting either is
  // the follow-up ticket's job, not a hotfix's.
  return true
  // eslint-disable-next-line no-unreachable
  const rawValue = user?.v3_user

  // allow all devs to view
  if (user && user?.permissions && user.permissions.length > 0 && user.permissions.includes('dev-tools')) {
    return true
  }

  if (typeof rawValue === 'undefined' || rawValue === null) return undefined

  if (typeof rawValue === 'boolean') return rawValue
  if (rawValue === 1 || rawValue === '1') return true
  if (rawValue === 0 || rawValue === '0') return false

   if (typeof rawValue === 'string') {
    const normalizedValue = rawValue.trim().toLowerCase()

    if (['true', 'yes', 'on', 'enabled'].includes(normalizedValue)) return true
    if (['false', 'no', 'off', 'disabled', ''].includes(normalizedValue)) return false
  }

  return !!rawValue
}

export function getUserDashboardV3Enabled(user) {
  return getUserDashboardV3Preference(user) === true
}

export function getClientDashboardV3Enabled(client, user) {
  const userPreference = getUserDashboardV3Preference(user)

  if (userPreference === true) {
    return true
  }

  if (userPreference === false) {
    return false
  }

  return getClientFeatureFlagEnabled(client, CLIENT_DASHBOARD_V3_FLAG)
}

export function mergeClientDashboardV3Flag(client, isEnabled) {
  return mergeClientFeatureFlag(client, CLIENT_DASHBOARD_V3_FLAG, isEnabled)
}

export function getClientDashboardV3DisplayValue(client) {
  return getClientFeatureFlagDisplayValue(client, CLIENT_DASHBOARD_V3_FLAG)
}

export function buildV3HomePath() {
  return '/v3/home'
}

function getUserRoleTags(user) {
  const roleTags = Array.isArray(user?.roleTags)
    ? user.roleTags
      .map((tag) => {
        if (tag && typeof tag === 'object') {
          return tag.id ?? tag.roleId ?? tag.value ?? tag.tag ?? tag.name ?? null
        }

        return tag
      })
      .filter((tag) => tag !== null && typeof tag !== 'undefined')
      .map((tag) => String(tag))
    : []

  return [...new Set(roleTags)]
}

function getUniqueUserRoles(user) {
  if (!Array.isArray(user?.roles)) {
    return []
  }

  const rolesByIdentity = new Map()

  user.roles.forEach((role) => {
    if (!role || typeof role !== 'object') {
      return
    }

    const id = role.id ?? role.auth_role_id ?? role.roleId ?? role.role_id
    const name = String(role.name ?? role.roleName ?? '').trim().toLowerCase()
    const identity = id !== null && typeof id !== 'undefined'
      ? `id:${String(id)}`
      : name ? `name:${name}` : null

    if (identity && !rolesByIdentity.has(identity)) {
      rolesByIdentity.set(identity, {
        id: id !== null && typeof id !== 'undefined' ? String(id) : '',
        name,
      })
    }
  })

  return [...rolesByIdentity.values()]
}

function isActiveClientAssignment(client) {
  if (!client || typeof client !== 'object') {
    return false
  }

  const clientCode = client.ClientCode || client.clientCode
  if (!clientCode) {
    return false
  }

  if (client.disabled === true) return false
  if (client.isDisabled === true) return false
  if (client.active === false) return false
  if (client.isActive === false) return false
  if (client.deleted === true) return false
  if (client.deleted_at) return false
  if (client.deletedAt) return false

  return true
}

export function getSingleAssignedClient(user) {
  if (isActiveClientAssignment(user?.client)) {
    return user.client
  }

  const clientCollections = [user?.clients, user?.assignedClients, user?.clientList]
    .filter(Array.isArray)

  for (const clientList of clientCollections) {
    const activeClients = clientList.filter(isActiveClientAssignment)

    if (activeClients.length === 1) {
      return activeClients[0]
    }
  }

  return null
}

export function isClientOnlyUser(user) {
  const roles = getUniqueUserRoles(user)
  const roleTags = getUserRoleTags(user)
  const clientRoleId = String(constants.ROLE_ID.client)

  if (roles.length > 0) {
    return roles.length === 1
      && (roles[0].id === clientRoleId || roles[0].name === 'client')
  }

  if (roleTags.length > 0) {
    return roleTags.length === 1 && roleTags[0] === clientRoleId
  }

  return false
}

export function shouldBypassCrossClientDashboard(user) {
  return Boolean(isClientOnlyUser(user) && getSingleAssignedClient(user))
}

export function buildPostLoginPath(user) {
  const singleAssignedClient = getSingleAssignedClient(user)

  if (getUserDashboardV3Enabled(user) && shouldBypassCrossClientDashboard(user) && singleAssignedClient) {
    return buildV3ClientDashboardPath(singleAssignedClient.ClientCode || singleAssignedClient.clientCode)
  }

  return getUserDashboardV3Enabled(user)
    ? buildV3HomePath()
    : '/users'
}

export function buildV3CrossClientDashboardPath() {
  return '/v3/cross-client-dashboard'
}

export function buildV3CopyPlanPath() {
  return '/v3/plan/copy'
}

export function buildV3CopyPlanUploadPath() {
  return '/v3/plan/copy/upload'
}

export function buildV3CopyPlanFileDetailPath(id, mode) {
  return mode === 'line'
    ? `/v3/plan/copy/line/${encodePathSegment(id)}`
    : `/v3/plan/copy/file/${encodePathSegment(id)}`
}

export function buildV3AddClientPath() {
  return '/v3/client/add'
}

export function buildV3ClientSetupPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/setup`
}

export function buildV3AdminCustomCalendarPath() {
  return '/v3/admin/custom-calendar-management'
}

export function buildV3UsersPath() {
  return '/v3/users'
}

export function buildV3UserDetailPath(userId) {
  return `/v3/users/${encodePathSegment(userId)}`
}

export function buildV3AdminUserRolesPath() {
  return '/v3/admin/user-roles'
}

export function buildV3AdminPermissionsPath() {
  return '/v3/admin/permissions'
}

export function buildV3AdminOrbitMetadataPath() {
  return '/v3/admin/orbit-metadata'
}

export function buildV3AdminContentReviewPath() {
  return '/v3/admin/content-review'
}

export function buildV3AdminOrbitAiPath() {
  return '/v3/admin/orbit-ai/forecast-runs'
}

export function buildV3AdminClientNotificationsPath() {
  return '/v3/admin/client-notifications'
}

export function buildV3AdminM1AdvisorsPath() {
  return '/v3/admin/m1-advisors'
}

export function buildV3AdminFlightControlPath() {
  return '/v3/admin/flightcontrol'
}

export function buildV3DevToolsPath() {
  return '/v3/dev-tools'
}

export function buildLegacyClientDashboardPath(clientCode) {
  return `/client/${encodePathSegment(clientCode)}/simple-dashboard`
}

export function buildLegacyClientLivePlanEditPath(clientCode) {
  return `/clients/${encodePathSegment(clientCode)}/live-plan-edit`
}

export function buildLegacyClientLivePlanEditWeeklyPath(clientCode) {
  return `/clients/${encodePathSegment(clientCode)}/live-plan-edit-weekly`
}

export function buildV3ClientDashboardPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/simple-dashboard`
}

export function buildClientDashboardPath({ clientCode, client, user, preferredMode }) {
  if (preferredMode === 'v3') {
    return buildV3ClientDashboardPath(clientCode)
  }

  if (preferredMode === 'legacy') {
    return buildLegacyClientDashboardPath(clientCode)
  }

  return getClientDashboardV3Enabled(client, user)
    ? buildV3ClientDashboardPath(clientCode)
    : buildLegacyClientDashboardPath(clientCode)
}

export function buildV3ClientIndieAiPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/indie-ai`
}

export function buildV3ClientAnalyticsPath(clientCode, clientId) {
  return `/v3/client/${encodePathSegment(clientCode)}/analytics/${encodePathSegment(clientId)}`
}

export function buildV3ClientUploadDataPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/upload-data`
}

export function buildV3ClientLivePlanEditPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/live-plan-edit`
}

export function buildV3ClientLivePlanEditWeeklyPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/live-plan-edit-weekly`
}

export function getClientWeeklyPlanUiEnabled(client) {
  return getClientFeatureFlagEnabled(client, WEEKLY_PLAN_UI_FLAG)
}

export function buildClientLivePlanEditPath({ clientCode, client, preferredMode = undefined }) {
  const useWeeklyPlanner = getClientWeeklyPlanUiEnabled(client)

  if (preferredMode === 'v3') {
    return useWeeklyPlanner
      ? buildV3ClientLivePlanEditWeeklyPath(clientCode)
      : buildV3ClientLivePlanEditPath(clientCode)
  }

  if (preferredMode === 'legacy') {
    return useWeeklyPlanner
      ? buildLegacyClientLivePlanEditWeeklyPath(clientCode)
      : buildLegacyClientLivePlanEditPath(clientCode)
  }

  return getClientDashboardV3Enabled(client)
    ? (useWeeklyPlanner
      ? buildV3ClientLivePlanEditWeeklyPath(clientCode)
      : buildV3ClientLivePlanEditPath(clientCode))
    : (useWeeklyPlanner
      ? buildLegacyClientLivePlanEditWeeklyPath(clientCode)
      : buildLegacyClientLivePlanEditPath(clientCode))
}

/**
 * @param {{
 *   clientCode: string,
 *   client: any,
 *   preferredMode?: 'v3' | 'legacy' | undefined,
 * }} params
 */
export function buildClientPlanPath({ clientCode, client, preferredMode = undefined }) {
  const useV3Shell = preferredMode === 'v3'
  const useWeeklyPlanner = getClientWeeklyPlanUiEnabled(client)

  return useV3Shell
    ? (useWeeklyPlanner
      ? buildV3ClientLivePlanEditWeeklyPath(clientCode)
      : buildV3ClientLivePlanEditPath(clientCode))
    : (useWeeklyPlanner
      ? buildLegacyClientLivePlanEditWeeklyPath(clientCode)
      : buildLegacyClientLivePlanEditPath(clientCode))
}


export function buildV3ClientPlanPreviewPath(clientCode, clientId) {
  return `/v3/client/${encodePathSegment(clientCode)}/planninig/${encodePathSegment(clientId)}/preview`
}

export function buildV3ClientOtherPlanToolsPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/plan/other-tools`
}

export function buildV3ClientProfilePath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/profile`
}

export function buildV3ClientLocationsPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/locations`
}

export function buildV3ClientClassesPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/classes`
}

export function buildV3ClientCategoriesPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/categories`
}

export function buildV3ClientImportClassPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/import-class`
}

export function buildV3ClientPosMappingPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/pos-mapping`
}

export function buildV3ClientMasterClassPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/master-class`
}

export function buildV3ClientTagsPath(clientCode) {
  return `/v3/client/${encodePathSegment(clientCode)}/tags`
}

export function buildV3ClientLegacyReportLibraryPath(clientCode, clientId) {
  return `/v3/client/${encodePathSegment(clientCode)}/report-library/${encodePathSegment(clientId)}`
}

export function buildV3ClientDashboardPerformancePath(clientCode) {
  return `${buildV3ClientDashboardPath(clientCode)}/performance`
}

export function buildV3ClientDashboardReportsPath(clientCode, clientId) {
  return `${buildV3ClientDashboardPath(clientCode)}/reports/${encodePathSegment(clientId)}`
}

export function buildClientReportsLibraryPath({ clientCode, clientId, isV3 = false }) {
  if (isV3) {
    return buildV3ClientDashboardReportsPath(clientCode, clientId)
  }

  return `/clients/${encodePathSegment(clientCode)}/reports-lib/${encodePathSegment(clientId)}`
}

export function buildClientCustomReportCreatePath({ clientCode, clientId, isV3 = false }) {
  if (isV3) {
    return `${buildV3ClientDashboardReportsPath(clientCode, clientId)}/create`
  }

  return `/clients/${encodePathSegment(clientCode)}/reports-lib/${encodePathSegment(clientId)}/create`
}

export function buildClientCustomReportEditPath({ clientCode, clientId, filterId, isV3 = false }) {
  if (isV3) {
    return `${buildV3ClientDashboardReportsPath(clientCode, clientId)}/edit/${encodePathSegment(filterId)}`
  }

  return `/clients/${encodePathSegment(clientCode)}/reports-lib/${encodePathSegment(clientId)}/edit/${encodePathSegment(filterId)}`
}

export function isV3ClientDashboardPath(pathname) {
  return typeof pathname === 'string'
    && pathname.startsWith('/v3/client/')
    && pathname.includes('/simple-dashboard')
}

